import dayjs, { Dayjs } from 'dayjs';
import { FloatingElement, FloatingElementOptions, useFloatingElement } from 'library/floating';
import { DetailedHTMLProps, FC, InputHTMLAttributes, ReactNode, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import Picker from './Picker';

// value
// onChange
// format
// excludeDates
// includeDates
// minDate
// maxDate
// highlightDates

interface InputProps
    extends Omit<
        DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
        'ref' | 'value' | 'onChange' | 'size'
    > {}

interface DatePickerProps extends InputProps, Pick<FloatingElementOptions, 'placement'> {
    value?: Date;
    onChange?: (date: Date) => void;
    format?: string;
    minDate?: Date;
    maxDate?: Date;
    includeDates?: Date[];
    renderInput?: (inputProps: InputProps) => ReactNode;
}
const DatePicker: FC<DatePickerProps> = (props) => {
    const {
        value: controlledValue,
        onChange,
        minDate,
        maxDate,
        includeDates,
        renderInput,
        className,
        placement = 'bottom-start',
        format = 'DD MMM, YYYY',
        placeholder = 'Select from calendar',
        readOnly = true,
        ...rest
    } = props;

    const [unControlledValue, setUnControlledValue] = useState(dayjs());

    const value = controlledValue !== undefined ? dayjs(controlledValue) : unControlledValue;
    const setValue = controlledValue !== undefined ? undefined : setUnControlledValue;

    const { context, refs, getReferenceProps, getFloatingProps } = useFloatingElement({
        placement,
        offset: 8,
    });

    const inputProps = useMemo(() => {
        return {
            ...rest,
            ...getReferenceProps(),
            ref: refs.setReference,
            className: twMerge('caret-transparent cursor-pointer bg-transparent', className),
            value: value && value.isValid() ? value.format(format) : '',
            type: 'text',
            autoComplete: 'off',
            placeholder,
            readOnly,
        };
    }, [className, format, getReferenceProps, placeholder, readOnly, refs.setReference, rest, value]);

    const handleChange = (date: Dayjs) => {
        setValue?.(date);

        onChange?.(date.toDate());

        context.onOpenChange(false);
    };

    return (
        <>
            {renderInput ? renderInput(inputProps) : <input {...inputProps} />}

            <FloatingElement
                context={context}
                refs={refs}
                {...getFloatingProps()}
                className="p-4 rounded-xl shadow-[0px_1px_16px_2px_rgba(13,16,33,0.08),1px_6px_32px_rgba(5,22,35,0.06)] min-w-[326px] max-w-full min-h-[240px] select-none z-[2]"
            >
                <Picker
                    value={value}
                    onChange={handleChange}
                    minDate={minDate ? dayjs(minDate) : null}
                    maxDate={maxDate ? dayjs(maxDate) : null}
                    includeDates={includeDates ? includeDates.map((date) => dayjs(date)) : null}
                />
            </FloatingElement>
        </>
    );
};

export default DatePicker;
